import Auth from '../services/Auth';

class Client {
  constructor({ baseUrl, defaultHeaders } = {}) {
    this.baseUrl = baseUrl;
    this.defaultHeaders = defaultHeaders;
  }

  get(endpoint) {
    // Request with GET/HEAD method cannot have body
    return this.handleRequest(endpoint, 'GET');
  }

  post(endpoint, params) {
    return this.handleRequest(endpoint, 'POST', params);
  }

  patch(endpoint, params) {
    return this.handleRequest(endpoint, 'PATCH', params);
  }

  delete(endpoint, params) {
    return this.handleRequest(endpoint, 'DELETE', params);
  }

  handleResponse(response) {
    if (response.status === 401) {
      // Handle HTTP 401 (Unauthorized)
      return Auth.logout();
    }

    if (response.ok) {
      // Note that the promise won't be rejected in case of HTTP 4xx or 5xx server
      // responses. The promise will be resolved just as it would be for HTTP 2xx.
      // Inspect the response.status number within the resolved callback to add
      // conditional handling of server errors to your code.
      //
      // Reference: https://github.github.io/fetch/#error
      //
      return response;
    }

    const error = new Error(response.statusText);
    error.response = response;
    throw error;
  }

  processResponse(response) {
    return response.json();
  }

  buildHeaders() {
    const baseHeaders = {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    };

    const authorizationHeaders = {
      Authorization: `Bearer ${Auth.getToken()}`
    };

    return {
      ...baseHeaders,
      ...this.defaultHeaders,
      ...authorizationHeaders
    };
  }

  buildBody(params) {
    return JSON.stringify(params);
  }

  handleRequest(endpoint, method, params) {
    const url = `${this.baseUrl}/${endpoint}`;

    if (!Auth.getToken()) {
      return Promise.reject(new Error('no session available'));
    }

    return fetch(url, {
      headers: this.buildHeaders(),
      method,
      body: this.buildBody(params)
    })
      .then(this.handleResponse)
      .then(this.processResponse);
  }
}

class Api {
  constructor() {
    this.client = new Client({
      baseUrl: process.env.REACT_APP_API_URL
    });
  }

  getSystemStatus() {
    return this.client.get('system/health');
  }

  createSession(params) {
    return this.client.post('session', { session: params });
  }

  getSession() {
    return this.client.get('session');
  }

  updateProfile(params) {
    return this.client.patch('profile', { profile: params });
  }

  createRegistration(params) {
    return this.client.post('registration', { registration: params });
  }

  getTeams(params) {
    return this.client.get('teams', params);
  }

  getTeam(teamId) {
    return this.client.get(`teams/${teamId}`);
  }

  createTeam(params) {
    return this.client.post('teams', { team: params });
  }

  updateTeam(teamId, params = {}) {
    return this.client.patch(`teams/${teamId}`, { team: params });
  }

  deleteTeam(teamId, params) {
    return this.client.delete(`teams/${teamId}`, { team: params });
  }

  getTeamUsers(teamId) {
    return this.client.get(`teams/${teamId}/users`);
  }

  getTeamMembers(teamId) {
    return this.client.get(`teams/${teamId}/members`);
  }

  getTeamActivity(teamId, params) {
    return this.client.get(`teams/${teamId}/activity`, { activity: params });
  }

  createTeamMember(teamId, params) {
    return this.client.post(`teams/${teamId}/members`, { member: params });
  }

  updateTeamMember(teamId, teamMemberId, params) {
    return this.client.patch(`teams/${teamId}/members/${teamMemberId}`, {
      member: params
    });
  }

  deleteTeamMember(teamId, teamMemberId) {
    return this.client.delete(`teams/${teamId}/members/${teamMemberId}`);
  }

  getStatus(teamId) {
    return this.client.get(`teams/${teamId}/status`);
  }

  updateStatus(teamId, params) {
    return this.client.patch(`teams/${teamId}/status`, { status: params });
  }

  createSnapshot(teamId, params) {
    return this.client.post(`teams/${teamId}/snapshots`, { snapshot: params });
  }

  getInvitations() {
    return this.client.get('invitations');
  }

  acceptInvitation(invitationId) {
    return this.client.post(`invitations`, {
      invitation: { id: invitationId }
    });
  }

  deleteSlackStatusIntegration(teamId) {
    return this.client.delete(`teams/${teamId}/slack_status_integration`);
  }

  deleteSlackChatIntegration(teamId) {
    return this.client.delete(`teams/${teamId}/slack_chat_integration`);
  }
}

export default new Api();



// WEBPACK FOOTER //
// ./src/utils/Api.js